import { useCallback, useEffect, useRef, useState } from "react";

export type ScrollDirection = "up" | "down" | "none";

interface UseScrollDirectionOptions {
  threshold?: number;
  throttleMs?: number;
}

interface ScrollState {
  direction: ScrollDirection;
  scrollY: number;
  scrollDiff: number;
}

/**
 * 스크롤 방향과 스크롤 상태를 추적하는 훅
 */
export function useScrollDirection({
  threshold = 0,
  throttleMs = 100,
}: UseScrollDirectionOptions = {}): ScrollState {
  const [scrollState, setScrollState] = useState<ScrollState>({
    direction: "none",
    scrollY: 0,
    scrollDiff: 0,
  });

  const prevScrollRef = useRef<number>(0);
  const throttleTimerRef = useRef<number | null>(null);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDiff = currentScrollY - prevScrollRef.current;

    let direction: ScrollDirection = "none";
    if (scrollDiff > threshold) {
      direction = "down";
    } else if (scrollDiff < -threshold) {
      direction = "up";
    }

    setScrollState({
      direction,
      scrollY: currentScrollY,
      scrollDiff,
    });

    prevScrollRef.current = currentScrollY;
  }, [threshold]);

  const throttledHandleScroll = useCallback(() => {
    if (throttleTimerRef.current !== null) return;

    throttleTimerRef.current = window.setTimeout(() => {
      handleScroll();
      throttleTimerRef.current = null;
    }, throttleMs);
  }, [handleScroll, throttleMs]);

  useEffect(() => {
    prevScrollRef.current = window.scrollY;
    setScrollState({
      direction: "none",
      scrollY: window.scrollY,
      scrollDiff: 0,
    });

    window.addEventListener("scroll", throttledHandleScroll, true);

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll, true);
      if (throttleTimerRef.current !== null) {
        clearTimeout(throttleTimerRef.current);
      }
    };
  }, [throttledHandleScroll]);

  return scrollState;
}
